import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaFacebook, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { X, Phone } from "lucide-react";
import { Link } from "react-router-dom";

const SOCIAL = [
  { icon: FaFacebook,   href: "https://www.facebook.com/Terces1555",        label: "Facebook"  },
  { icon: FaInstagram,  href: "https://www.instagram.com/terces_solutions/", label: "Instagram" },
  { icon: FaLinkedinIn, href: "https://www.linkedin.com/company/105919678/", label: "LinkedIn"  },
];

const WHATSAPP_URL =
  "https://wa.me/919281115822?text=Hi%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20restaurant%20consulting%20services";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const GlobalFloating = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const show = visible && !dismissed;

  return (
    <>
      {/* Social Icons — fixed right edge */}
      <div
        className="hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-[990] flex-col items-center gap-4 py-4 px-[11px] rounded-l-2xl shadow-xl"
        style={{ background: "rgba(20,20,20,0.82)", backdropFilter: "blur(6px)" }}
      >
        {SOCIAL.map(({ icon: Icon, href, label }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={label}
            className="text-white/65 hover:text-white transition-colors duration-200"
          >
            <Icon size={16} />
          </a>
        ))}
      </div>

      {/* WhatsApp CTA — fixed bottom-right */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-7 right-7 z-[990] flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transition-transform duration-200 hover:scale-110"
        style={{ background: "#25D366" }}
      >
        <WhatsAppIcon />
      </a>

      {/* Strategy Call CTA — fixed bottom-left */}
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.88 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 340, damping: 26 }}
            className="fixed bottom-7 left-7 z-[990]"
            style={{ width: "160px" }}
          >
            {/* Pulse ring — subtle beacon behind the card */}
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{ background: "#1b7840" }}
              animate={{ scale: [1, 1.08, 1], opacity: [0.35, 0, 0.35] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Card */}
            <motion.div
              onHoverStart={() => setHovered(true)}
              onHoverEnd={() => setHovered(false)}
              whileHover={{ y: -3, boxShadow: "0 16px 32px rgba(27,120,64,0.35)" }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 400, damping: 22 }}
              className="relative rounded-2xl overflow-hidden shadow-xl"
              style={{ background: "linear-gradient(135deg, #1f8a4a 0%, #1b7840 60%, #155a30 100%)" }}
            >
              {/* Shimmer sweep on hover */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.12) 50%, transparent 60%)",
                }}
                animate={hovered ? { x: ["−100%", "200%"] } : { x: "-100%" }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              />

              {/* Dismiss */}
              <motion.button
                onClick={() => setDismissed(true)}
                aria-label="Dismiss"
                whileHover={{ scale: 1.2, backgroundColor: "rgba(255,255,255,0.35)" }}
                whileTap={{ scale: 0.9 }}
                className="absolute top-2 right-2 w-4 h-4 rounded-full bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-2.5 h-2.5 text-white" strokeWidth={3} />
              </motion.button>

              <Link
                to="/Contact"
                className="flex items-center gap-2 px-3.5 py-3"
                style={{ textDecoration: "none" }}
              >
                {/* Phone icon with ring animation */}
                <motion.div
                  animate={{ rotate: [0, -12, 12, -8, 8, 0] }}
                  transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 3, ease: "easeInOut" }}
                >
                  <Phone className="w-3 h-3 text-white/80 shrink-0" strokeWidth={2} />
                </motion.div>

                <span className="text-white text-xs font-semibold leading-snug">
                  Book a Free<br />Strategy Call
                </span>
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GlobalFloating;
